/**
 * Backend Apps Service
 * Handles all app-related API calls to the FastAPI backend.
 * Uses simplified X-User-Id authentication for Azure deployment.
 */

import { apiRequest } from '@/lib/api-client'
import { logError, logInfo } from '@/lib/logger'

// ============== Types ==============

// How a URL app opens (#1505): framed in a desktop window, or in a new browser tab.
export type ExternalOpenMode = 'window' | 'tab'

// How an app relates to knowledge areas (cdb_tqs#936).
export interface RegisteredApp {
  id: string
  name: string
  display_name: string
  description: string | null
  icon: string | null
  category: string
  is_active: boolean
  knowledge_area_aware: boolean
  external_url?: string | null
  open_mode?: ExternalOpenMode | null
  /** The window hands this URL app the user's token (#1506), decided by the BFF. */
  sso?: boolean
  created_at: string
  updated_at: string
}

export interface CreateAppData {
  name: string
  display_name: string
  description?: string
  icon?: string
  category?: string
  is_active?: boolean
  knowledge_area_aware?: boolean
  external_url?: string
  open_mode?: ExternalOpenMode
}

export interface UpdateAppData {
  name?: string
  display_name?: string
  description?: string
  icon?: string
  category?: string
  is_active?: boolean
  knowledge_area_aware?: boolean
  external_url?: string | null
  open_mode?: ExternalOpenMode
}

interface AppListResponse {
  apps: RegisteredApp[]
  total: number
}

// ============== Backend Apps Service ==============

export const backendAppsService = {
  // ============== Apps CRUD ==============

  async getApps(): Promise<RegisteredApp[]> {
    try {
      const response = await apiRequest<AppListResponse>('/apps')
      logInfo({ context: 'BackendAppsService' }, `Loaded ${response?.apps.length ?? 0} apps`)
      return response?.apps ?? []
    } catch (error) {
      logError(error as Error, { context: 'BackendAppsService', method: 'getApps' })
      return []
    }
  },

  async getActiveApps(): Promise<RegisteredApp[]> {
    try {
      const response = await apiRequest<AppListResponse>('/apps/active')
      logInfo({ context: 'BackendAppsService' }, `Loaded ${response?.apps.length ?? 0} active apps`)
      return response?.apps ?? []
    } catch (error) {
      logError(error as Error, { context: 'BackendAppsService', method: 'getActiveApps' })
      return []
    }
  },

  async getAppById(appId: string): Promise<RegisteredApp | null> {
    try {
      return await apiRequest<RegisteredApp>(`/apps/${appId}`)
    } catch (error) {
      logError(error as Error, { context: 'BackendAppsService', method: 'getAppById', appId })
      return null
    }
  },

  async getAppByName(name: string): Promise<RegisteredApp | null> {
    try {
      return await apiRequest<RegisteredApp>(`/apps/${name}`)
    } catch (error) {
      logError(error as Error, { context: 'BackendAppsService', method: 'getAppByName', name })
      return null
    }
  },

  async createApp(appData: CreateAppData): Promise<RegisteredApp | null> {
    try {
      const created = await apiRequest<RegisteredApp>('/apps', {
        method: 'POST',
        body: JSON.stringify(appData),
      })
      if (created) {
        logInfo({ context: 'BackendAppsService' }, `Created app: ${created.name}`)
      }
      return created
    } catch (error) {
      logError(error as Error, { context: 'BackendAppsService', method: 'createApp' })
      throw error
    }
  },

  async updateApp(appId: string, updates: UpdateAppData): Promise<RegisteredApp | null> {
    try {
      const updated = await apiRequest<RegisteredApp>(`/apps/${appId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
      if (updated) {
        logInfo({ context: 'BackendAppsService' }, `Updated app: ${updated.name}`)
      }
      return updated
    } catch (error) {
      logError(error as Error, { context: 'BackendAppsService', method: 'updateApp', appId })
      throw error
    }
  },

  async deleteApp(appId: string): Promise<boolean> {
    try {
      await apiRequest(`/apps/${appId}`, { method: 'DELETE' })
      logInfo({ context: 'BackendAppsService' }, `Deleted app: ${appId}`)
      return true
    } catch (error) {
      logError(error as Error, { context: 'BackendAppsService', method: 'deleteApp', appId })
      throw error
    }
  },

  // ============== Categories ==============

  async getCategories(): Promise<string[]> {
    try {
      const response = await apiRequest<{ categories: string[] }>('/apps/categories/list')
      return response?.categories ?? []
    } catch (error) {
      logError(error as Error, { context: 'BackendAppsService', method: 'getCategories' })
      return []
    }
  },
}
